import * as React from 'react';
import {
    Checkbox,
    CommandBar,
    DefaultButton,
    DetailsList,
    DetailsListLayoutMode,
    DetailsRow,
    Dropdown,
    DropdownMenuItemType,
    IDropdownOption,
    IColumn,
    ICommandBarItemProps,
    IDetailsRowProps,
    IconButton,
    Label,
    MessageBar,
    MessageBarType,
    mergeStyleSets,
    Panel,
    PanelType,
    Selection,
    SelectionMode,
    Separator,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    TextField,
} from '@fluentui/react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApiError, ApiHelper } from '../../../ApiHelper';
import {
    AgentTaskListDetailResult,
    AgentTaskLifecycleStatus,
    AgentTaskListRowModel,
    AgentTaskSnapshotSummary,
    AgentTaskPullRequestSummary,
    AgentTaskProposalVoteUpdateResult,
    AgentTaskTaskDetailResult,
    AgentTaskTaskListResult,
    AgentTaskTaskSummary,
    RepairAgentTaskPullRequestResult,
    PAGE_SIZE_OPTIONS,
    PageShell,
    StatCard,
    SyntheticBanner,
    TaskListSortDirection,
    TaskListSortKey,
    buildTaskListDocumentTitle,
    buildTaskListRowModel,
    compareStrings,
    compareTaskListRows,
    formatCompactDate,
    formatDate,
    formatGranularity,
    formatNumber,
    formatTaskListScope,
    getDefaultTaskListSortDirection,
    getFlagLabel,
    getJobStatusLabel,
    getPullRequestStatusLabel,
    getPullRequestRepairSuccessMessage,
    getTaskTypeLabel,
    getTaskLifecycleStatusLabel,
    isTaskListSortKey,
    parseOptionalInt,
    parseOptionalJobStatus,
    parseOptionalPullRequestStatus,
    parseOptionalTaskLifecycleStatus,
    parseTaskListSortDirection,
    renderLinkedIdentifier,
    renderCopyableDetailsField,
    renderTaskJobs,
    taskHasWorkItemForJob,
    updateSearchParams,
    useCurrentService,
    useDocumentTitle,
} from '../shared';
import { BatchUpdateWorkItemsPanel } from '../panels/BatchUpdateWorkItemsPanel';
import { CreateSnapshotPanel } from '../panels/CreateSnapshotPanel';
import { EnqueueTasksPanel } from '../panels/EnqueueTasksPanel';
import { TaskDetailPanel } from '../panels/TaskDetailPanel';
import { PullRequestDetailPanel } from '../panels/PullRequestDetailPanel';
import { PullRequestSummaryList } from '../details/PullRequestSummaryList';
import { normalizeAgentTaskTaskListResult } from '../workItemApi';

const taskListClassNames = mergeStyleSets({
    lastViewedRow: {
        backgroundColor: '#f3f2f1',
    },
});

function buildTaskListRowWithGainEstimate(
    task: AgentTaskTaskSummary,
    selectedJobName?: string,
): AgentTaskListRowModel {
    const row = buildTaskListRowModel(task, selectedJobName);
    const reductions = row.displayWorkItems
        .map(workItem => workItem.estimatedGain)
        .filter((value): value is number =>
            value !== undefined
            && value !== null
            && Number.isFinite(value)
            && value >= 0
            && value <= 100);
    const expectedReductionPercent = reductions.length > 0 ? Math.max(...reductions) : undefined;

    return {
        ...row,
        gainEstimatePercent: expectedReductionPercent === undefined
            ? undefined
            : task.inclusive * expectedReductionPercent / 100,
    };
}

function getTaskDetailCacheKey(taskId: number, snapshotId?: number) {
    return `${taskId}:${snapshotId ?? 'latest'}`;
}

type TaskNavigationDirection = 'previous' | 'next';

const STATUS_FILTER_DROPDOWN_STYLES = {
    dropdownDivider: {
        height: 1,
        margin: '4px 0',
        backgroundColor: '#a19f9d',
    },
};

export function AgentTaskListPage() {
    const navigate = useNavigate();
    const { serviceName, taskListId } = useParams();
    const service = useCurrentService(serviceName);
    const [searchParams, setSearchParams] = useSearchParams();
    const snapshotId = parseOptionalInt(searchParams.get('snapshotId'));
    const taskId = parseOptionalInt(searchParams.get('taskId'));
    const prId = parseOptionalInt(searchParams.get('prId'));
    const requestedReturnTo = searchParams.get('returnTo');
    const autoPrPath = serviceName ? `/service/${serviceName}/agenttasks/autopr` : undefined;
    const returnToAutoPr = autoPrPath
        && (requestedReturnTo === autoPrPath || requestedReturnTo?.startsWith(`${autoPrPath}?`))
        ? requestedReturnTo
        : undefined;
    const pageNumber = Math.max(0, parseOptionalInt(searchParams.get('pageNumber')) ?? 0);
    const pageSize = Math.max(1, parseOptionalInt(searchParams.get('pageSize')) ?? 50);
    const search = searchParams.get('search') ?? '';
    const searchMode = searchParams.get('searchMode');
    const sortByParam = searchParams.get('sortBy');
    const sortBy: TaskListSortKey = isTaskListSortKey(sortByParam)
        ? sortByParam
        : 'exclusive';
    const sortDirection = parseTaskListSortDirection(searchParams.get('sortDir'))
        ?? getDefaultTaskListSortDirection(sortBy);
    const selectedJobName = searchParams.get('job')?.trim() || undefined;
    const jobStatusFilter = selectedJobName
        ? parseOptionalJobStatus(searchParams.get('jobStatus'))
        : undefined;
    const pullRequestStatusParam = searchParams.get('pullRequestStatus');
    const excludeCompletedPullRequests = pullRequestStatusParam === 'allButCompleted';
    const pullRequestStatusFilter = excludeCompletedPullRequests
        ? undefined
        : parseOptionalPullRequestStatus(pullRequestStatusParam);
    const taskStatusParam = searchParams.get('taskStatus');
    const excludeDoneTasks = taskStatusParam === 'allButDone';
    const taskStatusFilter = excludeDoneTasks
        ? undefined
        : parseOptionalTaskLifecycleStatus(taskStatusParam);
    const [searchText, setSearchText] = React.useState(search);
    const [detailLoading, setDetailLoading] = React.useState(true);
    const [detailError, setDetailError] = React.useState<string | null>(null);
    const [detail, setDetail] = React.useState<AgentTaskListDetailResult | null>(null);
    const [tasksLoading, setTasksLoading] = React.useState(true);
    const [tasksError, setTasksError] = React.useState<string | null>(null);
    const [tasksResult, setTasksResult] = React.useState<AgentTaskTaskListResult | null>(null);
    const [lastViewedTaskId, setLastViewedTaskId] = React.useState<number | undefined>();
    const [columnResizeVersion, setColumnResizeVersion] = React.useState(0);
    const [taskNavigationDirection, setTaskNavigationDirection] =
        React.useState<TaskNavigationDirection | undefined>();
    const [, setTaskDetailCacheVersion] = React.useState(0);
    const prefetchedTaskDetails = React.useRef(new Map<string, AgentTaskTaskDetailResult>());
    const prefetchingTaskDetails = React.useRef(new Set<string>());
    const [referenceResult, setReferenceResult] = React.useState<AgentTaskTaskListResult | null>(null);
    const [referenceSearch, setReferenceSearch] = React.useState('');
    const [referenceLoading, setReferenceLoading] = React.useState(false);
    const [referenceError, setReferenceError] = React.useState<string | null>(null);
    const referenceRequestId = React.useRef(0);
    const lastReferenceRequestKey = React.useRef('');
    const [isSnapshotPanelOpen, setSnapshotPanelOpen] = React.useState(false);
    const [isEnqueuePanelOpen, setEnqueuePanelOpen] = React.useState(false);
    const [isBatchUpdatePanelOpen, setBatchUpdatePanelOpen] = React.useState(false);
    const [selectedTaskIds, setSelectedTaskIds] = React.useState<number[]>([]);
    const [resolvingPullRequestIds, setResolvingPullRequestIds] = React.useState<Set<number>>(
        () => new Set<number>(),
    );
    // A refresh intentionally permits a new assessment because PR evidence may have changed.
    const [nonActionablePullRequestIds, setNonActionablePullRequestIds] = React.useState<Set<number>>(
        () => new Set<number>(),
    );
    const [repairError, setRepairError] = React.useState<string | null>(null);
    const isRegressionList = detail?.taskList.taskType === 4;
    const [repairSuccess, setRepairSuccess] = React.useState<string | null>(null);

    const numericTaskListId = taskListId ? Number.parseInt(taskListId, 10) : NaN;
    useDocumentTitle(
        detail
            ? buildTaskListDocumentTitle(detail.taskList.name, detail.taskList.generation, numericTaskListId)
            : 'Agent Task List',
    );
    const selection = React.useMemo(
        () => {
            const taskSelection = new Selection({
                onSelectionChanged: () => {
                    const selected = taskSelection.getSelection() as AgentTaskListRowModel[];
                    setSelectedTaskIds(selected.map(item => item.task.taskId));
                },
            });
            return taskSelection;
        },
        [],
    );

    const loadDetail = React.useCallback(() => {
        if (!serviceName || !Number.isFinite(numericTaskListId)) {
            return;
        }

        setDetailLoading(true);
        setDetailError(null);

        ApiHelper(`/api/service/${serviceName}/agenttasklists/${numericTaskListId}`)
            .then((response: AgentTaskListDetailResult) => {
                setDetail(response);
                setDetailLoading(false);
            })
            .catch((err: Error) => {
                setDetailError(err.message || 'Failed to load the task list.');
                setDetail(null);
                setDetailLoading(false);
            });
    }, [numericTaskListId, serviceName]);

    const loadTasks = React.useCallback(() => {
        if (!serviceName || !Number.isFinite(numericTaskListId)) {
            return;
        }

        setTasksLoading(true);
        setTasksError(null);
        const query = new URLSearchParams();
        if (snapshotId !== undefined) {
            query.set('snapshotId', String(snapshotId));
        }

        ApiHelper(`/api/service/${serviceName}/agenttasklists/${numericTaskListId}/tasks?${query.toString()}`)
            .then((response: AgentTaskTaskListResult) => {
                setTasksResult(normalizeAgentTaskTaskListResult(response));
                setTasksLoading(false);
            })
            .catch((err: Error) => {
                setTasksError(err.message || 'Failed to load tasks.');
                setTasksResult(null);
                setTasksLoading(false);
            });
    }, [numericTaskListId, serviceName, snapshotId]);

    const updateTaskProposalCounts = React.useCallback((
        updatedTaskId: number,
        counts: Pick<
            AgentTaskProposalVoteUpdateResult,
            'activeProposalCount' | 'votedProposalCount' | 'okProposalCount'
        >,
    ) => {
        const applyCounts = (current: AgentTaskTaskListResult | null) => current ? {
            ...current,
            tasks: current.tasks.map(task => task.taskId === updatedTaskId ? {
                ...task,
                ...counts,
            } : task),
        } : current;

        setTasksResult(applyCounts);
        setReferenceResult(applyCounts);
    }, []);

    React.useEffect(() => {
        setSearchText(search);
    }, [search]);

    React.useEffect(() => {
        if (searchText === search) {
            return;
        }

        const timeout = window.setTimeout(() => {
            updateSearchParams(searchParams, setSearchParams, {
                search: searchText,
                searchMode: null,
                pageNumber: 0,
            });
        }, 250);
        return () => window.clearTimeout(timeout);
    }, [search, searchParams, searchText, setSearchParams]);

    React.useEffect(() => {
        loadDetail();
    }, [loadDetail]);

    React.useEffect(() => {
        if (snapshotId === undefined
            && detail?.taskList.taskType !== 4
            && detail?.latestSnapshot?.snapshotId) {
            updateSearchParams(searchParams, setSearchParams, {
                snapshotId: detail.latestSnapshot.snapshotId,
            });
        }
    }, [detail?.latestSnapshot?.snapshotId, detail?.taskList.taskType, searchParams, setSearchParams, snapshotId]);

    React.useEffect(() => {
        loadTasks();
    }, [loadTasks]);

    React.useEffect(() => {
        selection.setItems([], true);
        setSelectedTaskIds([]);
    }, [selection, snapshotId]);

    React.useEffect(() => {
        referenceRequestId.current++;
        lastReferenceRequestKey.current = '';
        setReferenceResult(null);
        setReferenceSearch('');
        setReferenceLoading(false);
        setReferenceError(null);
    }, [search, snapshotId]);

    React.useEffect(() => {
        if (searchMode !== 'references') {
            referenceRequestId.current++;
            lastReferenceRequestKey.current = '';
            setReferenceResult(null);
            setReferenceSearch('');
            setReferenceLoading(false);
            setReferenceError(null);
        }
    }, [searchMode]);

    const currentSnapshot = React.useMemo(() => {
        if (!detail) {
            return undefined;
        }

        const effectiveSnapshotId = snapshotId ?? detail.latestSnapshot?.snapshotId;
        return detail.snapshots.find(snapshot => snapshot.snapshotId === effectiveSnapshotId)
            ?? detail.latestSnapshot
            ?? detail.snapshots[0];
    }, [detail, snapshotId]);

    const availableJobNames = React.useMemo(
        () => Array.from(
            new Set((tasksResult?.tasks ?? []).flatMap(task => task.workItems.map(workItem => workItem.jobName))),
        ).sort((left, right) => compareStrings(left, right)),
        [tasksResult?.tasks],
    );
    const jobStatusOptions = React.useMemo<IDropdownOption[]>(() => [
        { key: 'all', text: 'All job statuses' },
        { key: 0, text: getJobStatusLabel(0) },
        { key: 1, text: getJobStatusLabel(1) },
        { key: 2, text: getJobStatusLabel(2) },
        { key: 3, text: getJobStatusLabel(3) },
        { key: 4, text: getJobStatusLabel(4) },
        { key: 5, text: getJobStatusLabel(5) },
        { key: 6, text: getJobStatusLabel(6) },
        { key: 7, text: getJobStatusLabel(7) },
    ], []);
    const pullRequestStatusOptions = React.useMemo<IDropdownOption[]>(() => [
        { key: 'all', text: 'All PR statuses' },
        { key: 'all-divider', text: '-', itemType: DropdownMenuItemType.Divider },
        { key: 0, text: getPullRequestStatusLabel(0) },
        { key: 1, text: getPullRequestStatusLabel(1) },
        { key: 2, text: getPullRequestStatusLabel(2) },
        { key: 3, text: getPullRequestStatusLabel(3) },
        { key: 4, text: getPullRequestStatusLabel(4) },
        { key: 5, text: getPullRequestStatusLabel(5) },
        { key: 6, text: getPullRequestStatusLabel(6) },
        { key: 7, text: getPullRequestStatusLabel(7) },
        { key: 8, text: getPullRequestStatusLabel(8) },
        { key: 'negative-filters', text: '-', itemType: DropdownMenuItemType.Divider },
        { key: 'allButCompleted', text: 'All but completed' },
    ], []);
    const taskStatusOptions = React.useMemo<IDropdownOption[]>(() => [
        { key: 'all', text: 'All task statuses' },
        { key: 'all-divider', text: '-', itemType: DropdownMenuItemType.Divider },
        ...([
            'notStarted',
            'analysisInProgress',
            'needsInput',
            'analyzed',
            'analyzedNoFix',
            'readyToCode',
            'codingInProgress',
            'prInProgress',
            'prBlocked',
            'done',
            'failed',
            'other',
        ] as AgentTaskLifecycleStatus[]).map(status => ({
            key: status,
            text: getTaskLifecycleStatusLabel(status),
        })),
        { key: 'negative-filters', text: '-', itemType: DropdownMenuItemType.Divider },
        { key: 'allButDone', text: 'All but Done' },
    ], []);
    const jobOptions = React.useMemo<IDropdownOption[]>(
        () => [
            { key: 'all', text: 'All jobs' },
            ...availableJobNames.map(jobName => ({ key: jobName, text: jobName })),
        ],
        [availableJobNames],
    );
    const allTaskRows = React.useMemo(
        () => (tasksResult?.tasks ?? [])
            .filter(task => (task.flags & 1) === 0)
            .map(task => buildTaskListRowWithGainEstimate(task, selectedJobName)),
        [selectedJobName, tasksResult?.tasks],
    );
    const normalizedSearch = search.trim().toLowerCase();
    const directScopeRows = React.useMemo(
        () => allTaskRows.filter(row =>
            !normalizedSearch || row.task.scope.toLowerCase().includes(normalizedSearch)),
        [allTaskRows, normalizedSearch],
    );
    const activeReferenceResult = referenceResult
        && searchMode === 'references'
        && referenceSearch === search.trim()
        && referenceResult.snapshotId === currentSnapshot?.snapshotId
        ? referenceResult
        : null;
    const referenceRows = React.useMemo(
        () => (activeReferenceResult?.tasks ?? [])
            .filter(task => (task.flags & 1) === 0)
            .map(task => buildTaskListRowWithGainEstimate(task, selectedJobName)),
        [activeReferenceResult?.tasks, selectedJobName],
    );
    const filteredTaskRows = React.useMemo(() => {
        const sourceRows = activeReferenceResult ? referenceRows : directScopeRows;
        const matchingRows = sourceRows.filter(row => {
            if (!taskHasWorkItemForJob(row.task, selectedJobName)) {
                return false;
            }
            if (jobStatusFilter !== undefined && !row.displayJobStatuses.includes(jobStatusFilter)) {
                return false;
            }

            if (pullRequestStatusFilter !== undefined && !row.displayPullRequestStatuses.includes(pullRequestStatusFilter)) {
                return false;
            }
            if (excludeCompletedPullRequests && row.displayPullRequestStatuses.includes(7)) {
                return false;
            }
            if (excludeDoneTasks && row.taskStatus === 'done') {
                return false;
            }
            if (taskStatusFilter !== undefined && row.taskStatus !== taskStatusFilter) {
                return false;
            }

            return true;
        });

        return [...matchingRows].sort((left, right) => {
            if (sortBy !== 'gainEstimate') {
                return compareTaskListRows(left, right, sortBy, sortDirection);
            }

            if (left.gainEstimatePercent === undefined || right.gainEstimatePercent === undefined) {
                if (left.gainEstimatePercent !== right.gainEstimatePercent) {
                    return left.gainEstimatePercent === undefined ? 1 : -1;
                }
            } else if (left.gainEstimatePercent !== right.gainEstimatePercent) {
                const comparison = left.gainEstimatePercent - right.gainEstimatePercent;
                return sortDirection === 'desc' ? -comparison : comparison;
            }
            return right.task.inclusive - left.task.inclusive || left.task.taskId - right.task.taskId;
        });
    }, [activeReferenceResult, directScopeRows, excludeCompletedPullRequests, excludeDoneTasks, jobStatusFilter, pullRequestStatusFilter, referenceRows, selectedJobName, sortBy, sortDirection, taskStatusFilter]);
    const loadedTaskCount = allTaskRows.length;
    const matchingTaskCount = filteredTaskRows.length;
    const hasNoDirectScopeMatches = normalizedSearch.length > 0 && directScopeRows.length === 0;
    const referenceResultsHiddenByFilters =
        !!activeReferenceResult
        && referenceRows.length > 0
        && matchingTaskCount === 0;
    const totalPages = Math.max(1, Math.ceil(matchingTaskCount / pageSize));
    const effectivePageNumber = Math.min(pageNumber, Math.max(0, totalPages - 1));
    const currentPage = effectivePageNumber + 1;
    const visibleTaskRows = React.useMemo(() => {
        const startIndex = effectivePageNumber * pageSize;
        return filteredTaskRows.slice(startIndex, startIndex + pageSize);
    }, [effectivePageNumber, filteredTaskRows, pageSize]);
    const viewedTaskIndex = taskId === undefined
        ? -1
        : filteredTaskRows.findIndex(row => row.task.taskId === taskId);

    React.useEffect(() => {
        if (taskId !== undefined) {
            setLastViewedTaskId(taskId);
        }
    }, [taskId]);

    const clearLastViewedTaskRow = React.useCallback((viewedTaskId: number) => {
        setLastViewedTaskId(undefined);

        if (document.activeElement instanceof HTMLElement) {
            const focusedRow = document.activeElement.closest<HTMLElement>('[data-task-id]');
            if (Number(focusedRow?.dataset.taskId) === viewedTaskId) {
                document.activeElement.blur();
            }
        }
    }, []);

    const handleTaskListMouseMove = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        if (lastViewedTaskId === undefined || !(event.target instanceof Element)) {
            return;
        }

        const hoveredRow = event.target.closest<HTMLElement>('[data-task-id]');
        const hoveredTaskId = Number(hoveredRow?.dataset.taskId);
        if (Number.isFinite(hoveredTaskId) && hoveredTaskId !== lastViewedTaskId) {
            clearLastViewedTaskRow(lastViewedTaskId);
        }
    }, [clearLastViewedTaskRow, lastViewedTaskId]);

    const getTaskRowSnapshotId = React.useCallback((row: AgentTaskListRowModel | undefined) =>
        isRegressionList
            ? row?.displayWorkItems[0]?.snapshotId
            : snapshotId, [isRegressionList, snapshotId]);

    const navigateToTaskIndex = React.useCallback((
        targetIndex: number,
        direction: TaskNavigationDirection,
    ) => {
        const target = filteredTaskRows[targetIndex];
        if (!target) {
            return;
        }

        setTaskNavigationDirection(direction);
        updateSearchParams(searchParams, setSearchParams, {
            taskId: target.task.taskId,
            prId: null,
            snapshotId: getTaskRowSnapshotId(target),
            pageNumber: Math.floor(targetIndex / pageSize),
        });
    }, [filteredTaskRows, getTaskRowSnapshotId, pageSize, searchParams, setSearchParams]);

    React.useEffect(() => {
        if (!serviceName || taskNavigationDirection === undefined || viewedTaskIndex < 0) {
            return;
        }

        const prefetchIndex = taskNavigationDirection === 'next'
            ? viewedTaskIndex + 1
            : viewedTaskIndex - 1;
        const target = filteredTaskRows[prefetchIndex];
        if (!target) {
            return;
        }

        const targetSnapshotId = getTaskRowSnapshotId(target);
        const cacheKey = getTaskDetailCacheKey(target.task.taskId, targetSnapshotId);
        if (prefetchedTaskDetails.current.has(cacheKey)
            || prefetchingTaskDetails.current.has(cacheKey)) {
            return;
        }

        prefetchingTaskDetails.current.add(cacheKey);
        const query = targetSnapshotId !== undefined ? `?snapshotId=${targetSnapshotId}` : '';
        let active = true;
        ApiHelper(
            `/api/service/${serviceName}/agenttasklists/${numericTaskListId}/tasks/${target.task.taskId}${query}`,
        )
            .then((response: AgentTaskTaskDetailResult) => {
                if (!active) {
                    return;
                }

                prefetchedTaskDetails.current.set(cacheKey, response);
                setTaskDetailCacheVersion(version => version + 1);
            })
            .catch(() => {
                // Prefetch is opportunistic; TaskDetailContent performs the normal request on a miss.
            })
            .finally(() => {
                prefetchingTaskDetails.current.delete(cacheKey);
            });

        return () => {
            active = false;
        };
    }, [
        filteredTaskRows,
        getTaskRowSnapshotId,
        numericTaskListId,
        serviceName,
        taskNavigationDirection,
        viewedTaskIndex,
    ]);

    const renderTaskRow = React.useCallback((rowProps?: IDetailsRowProps) => {
        if (!rowProps) {
            return null;
        }

        const rowTaskId = rowProps.item.task.taskId;
        const isLastViewed = rowTaskId === lastViewedTaskId;
        return (
            <div data-task-id={rowTaskId}>
                <DetailsRow
                    {...rowProps}
                    className={isLastViewed
                        ? `atmTaskList__lastViewedRow ${taskListClassNames.lastViewedRow}`
                        : undefined}
                />
            </div>
        );
    }, [lastViewedTaskId]);
    const handleTaskListColumnResize = React.useCallback(() => {
        // DetailsList caches rows separately from its header, so resizing must invalidate both.
        setColumnResizeVersion(version => version + 1);
    }, []);

    React.useEffect(() => {
        if (pageNumber !== effectivePageNumber) {
            updateSearchParams(searchParams, setSearchParams, { pageNumber: effectivePageNumber });
        }
    }, [effectivePageNumber, pageNumber, searchParams, setSearchParams]);

    React.useEffect(() => {
        selection.setItems(visibleTaskRows, true);
        setSelectedTaskIds([]);
    }, [selection, visibleTaskRows]);

    const updateSort = React.useCallback(
        (columnKey: string) => {
            if (!isTaskListSortKey(columnKey)) {
                return;
            }

            const nextDirection: TaskListSortDirection = sortBy === columnKey
                ? (sortDirection === 'asc' ? 'desc' : 'asc')
                : getDefaultTaskListSortDirection(columnKey);

            updateSearchParams(searchParams, setSearchParams, {
                sortBy: columnKey,
                sortDir: nextDirection,
                pageNumber: 0,
            });
        },
        [searchParams, setSearchParams, sortBy, sortDirection],
    );
    const resolvePullRequest = React.useCallback(async (
        taskId: number,
        pullRequest: AgentTaskPullRequestSummary,
    ) => {
        if (!serviceName || resolvingPullRequestIds.has(pullRequest.prId)) {
            return;
        }

        setResolvingPullRequestIds(current => new Set(current).add(pullRequest.prId));
        setRepairError(null);
        setRepairSuccess(null);
        try {
            const result = await ApiHelper<RepairAgentTaskPullRequestResult>(
                `/api/service/${serviceName}/agenttasklists/${numericTaskListId}/tasks/${taskId}/pullrequests/${pullRequest.prId}/resolve`,
                'POST',
                null,
            );
            setRepairSuccess(getPullRequestRepairSuccessMessage(result, pullRequest.prId));
            if (result.action !== 2) {
                setResolvingPullRequestIds(current => {
                    const next = new Set(current);
                    next.delete(pullRequest.prId);
                    return next;
                });
            }
            loadTasks();
        } catch (error) {
            setRepairError(error instanceof Error ? error.message : 'Failed to resolve the pull request.');
            const errorCode = error instanceof ApiError
                && typeof error.body === 'object'
                && error.body !== null
                && 'code' in error.body
                && typeof error.body.code === 'string'
                    ? error.body.code
                    : null;
            if (errorCode === 'PullRequestRepairNotActionable') {
                setNonActionablePullRequestIds(current => new Set(current).add(pullRequest.prId));
            }
            if (errorCode !== 'PullRequestResolutionAlreadyRunning') {
                setResolvingPullRequestIds(current => {
                    const next = new Set(current);
                    next.delete(pullRequest.prId);
                    return next;
                });
            } else {
                loadTasks();
            }
        }
    }, [loadTasks, numericTaskListId, resolvingPullRequestIds, serviceName]);
    const updatePullRequestResolutionState = React.useCallback((
        pullRequestId: number,
        state: 'resolving' | 'idle' | 'notActionable',
    ) => {
        setResolvingPullRequestIds(current => {
            const next = new Set(current);
            if (state === 'resolving') {
                next.add(pullRequestId);
            } else {
                next.delete(pullRequestId);
            }
            return next;
        });
        if (state === 'resolving') {
            setNonActionablePullRequestIds(current => {
                const next = new Set(current);
                next.delete(pullRequestId);
                return next;
            });
        } else if (state === 'notActionable') {
            setNonActionablePullRequestIds(current => new Set(current).add(pullRequestId));
        }
    }, []);
    const findReferencingTasks = React.useCallback(async (force = false) => {
        const requestedSearch = search.trim();
        const requestedSnapshotId = currentSnapshot?.snapshotId;
        if (!serviceName || !requestedSearch || !requestedSnapshotId) {
            return;
        }

        const requestKey = `${numericTaskListId}:${requestedSnapshotId}:${requestedSearch}`;
        if (!force && lastReferenceRequestKey.current === requestKey) {
            return;
        }

        lastReferenceRequestKey.current = requestKey;
        const requestId = ++referenceRequestId.current;
        setReferenceLoading(true);
        setReferenceError(null);
        setReferenceResult(null);
        setReferenceSearch(requestedSearch);
        const query = new URLSearchParams({
            snapshotId: String(requestedSnapshotId),
            search: requestedSearch,
        });

        try {
            const response = await ApiHelper(
                `/api/service/${serviceName}/agenttasklists/${numericTaskListId}/tasks/referencing?${query.toString()}`,
            ) as AgentTaskTaskListResult;
            if (requestId !== referenceRequestId.current) {
                return;
            }

            setReferenceResult(normalizeAgentTaskTaskListResult(response));
        } catch (error) {
            if (requestId !== referenceRequestId.current) {
                return;
            }

            setReferenceError(error instanceof Error ? error.message : 'Failed to find referencing tasks.');
        } finally {
            if (requestId === referenceRequestId.current) {
                setReferenceLoading(false);
            }
        }
    }, [currentSnapshot?.snapshotId, numericTaskListId, search, serviceName]);
    React.useEffect(() => {
        if (searchMode === 'references') {
            void findReferencingTasks();
        }
    }, [findReferencingTasks, searchMode]);
    const columns = React.useMemo<IColumn[]>(() => [
        {
            key: 'scope',
            name: isRegressionList ? 'Commit' : 'Scope',
            minWidth: 280,
            isResizable: true,
            isSorted: sortBy === 'scope',
            isSortedDescending: sortBy === 'scope' ? sortDirection === 'desc' : undefined,
            onColumnClick: (_, column) => column && updateSort(column.key),
            onRender: (item: AgentTaskListRowModel) => (
                <a
                    href="#"
                    data-selection-disabled
                    title={item.task.scope}
                    onMouseDown={event => event.stopPropagation()}
                    onClick={event => {
                        event.preventDefault();
                        event.stopPropagation();
                        updateSearchParams(searchParams, setSearchParams, {
                            taskId: item.task.taskId,
                            prId: null,
                            snapshotId: isRegressionList
                                ? item.displayWorkItems[0]?.snapshotId ?? null
                                : snapshotId,
                        });
                        setTaskNavigationDirection(undefined);
                    }}
                    style={{
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {formatTaskListScope(item.task.scope)}
                </a>
            ),
        },
        {
            key: 'inclusive',
            name: 'Inclusive',
            minWidth: 90,
            isResizable: true,
            isSorted: sortBy === 'inclusive',
            isSortedDescending: sortBy === 'inclusive' ? sortDirection === 'desc' : undefined,
            onColumnClick: (_, column) => column && updateSort(column.key),
            onRender: (item: AgentTaskListRowModel) => formatNumber(item.task.inclusive),
        },
        {
            key: 'exclusive',
            name: 'Exclusive',
            minWidth: 90,
            isResizable: true,
            isSorted: sortBy === 'exclusive',
            isSortedDescending: sortBy === 'exclusive' ? sortDirection === 'desc' : undefined,
            onColumnClick: (_, column) => column && updateSort(column.key),
            onRender: (item: AgentTaskListRowModel) => formatNumber(item.task.exclusive),
        },
        {
            key: 'gainEstimate',
            name: 'Gain estimate',
            minWidth: 110,
            isResizable: true,
            isSorted: sortBy === 'gainEstimate',
            isSortedDescending: sortBy === 'gainEstimate' ? sortDirection === 'desc' : undefined,
            onColumnClick: (_, column) => column && updateSort(column.key),
            onRender: (item: AgentTaskListRowModel) => item.gainEstimatePercent === undefined
                ? '-'
                : `${formatNumber(item.gainEstimatePercent)}%`,
        },
        {
            key: 'methodCount',
            name: 'Methods',
            minWidth: 80,
            isResizable: true,
            isSorted: sortBy === 'methodCount',
            isSortedDescending: sortBy === 'methodCount' ? sortDirection === 'desc' : undefined,
            onColumnClick: (_, column) => column && updateSort(column.key),
            onRender: (item: AgentTaskListRowModel) => item.task.methodCount,
        },
        {
            key: 'workItems',
            name: 'Jobs',
            minWidth: 180,
            isResizable: true,
            isMultiline: true,
            isSorted: sortBy === 'workItems',
            isSortedDescending: sortBy === 'workItems' ? sortDirection === 'desc' : undefined,
            onColumnClick: (_, column) => column && updateSort(column.key),
            onRender: (item: AgentTaskListRowModel) => renderTaskJobs(item.jobSummaryWorkItems),
        },
        {
            key: 'taskStatus',
            name: 'Task status',
            minWidth: 135,
            isResizable: true,
            onRender: (item: AgentTaskListRowModel) => getTaskLifecycleStatusLabel(item.taskStatus),
        },
        {
            key: 'pullRequestStatus',
            name: selectedJobName ? `PR status (${selectedJobName})` : 'PR status',
            minWidth: 170,
            isResizable: true,
            isMultiline: true,
            isSorted: sortBy === 'pullRequestStatus',
            isSortedDescending: sortBy === 'pullRequestStatus' ? sortDirection === 'desc' : undefined,
            onColumnClick: (_, column) => column && updateSort(column.key),
            onRender: (item: AgentTaskListRowModel) => (
                <PullRequestSummaryList
                    service={service}
                    pullRequests={item.displayPullRequests}
                    workItems={item.task.workItems}
                    getDetailUrl={pullRequest => {
                        const next = new URLSearchParams(searchParams);
                        next.set('taskId', String(item.task.taskId));
                        next.set('prId', String(pullRequest.prId));
                        return `?${next.toString()}`;
                    }}
                    onOpen={pullRequest => updateSearchParams(searchParams, setSearchParams, {
                        taskId: item.task.taskId,
                        prId: pullRequest.prId,
                    })}
                    onResolve={pullRequest => {
                        void resolvePullRequest(item.task.taskId, pullRequest);
                    }}
                    resolvingPullRequestIds={resolvingPullRequestIds}
                    nonActionablePullRequestIds={nonActionablePullRequestIds}
                />
            ),
        },
    ].filter(column => !isRegressionList || ![
        'inclusive',
        'exclusive',
        'methodCount',
    ].includes(column.key)), [
        currentSnapshot?.snapshotId,
        isRegressionList,
        numericTaskListId,
        nonActionablePullRequestIds,
        resolvingPullRequestIds,
        resolvePullRequest,
        searchParams,
        selectedJobName,
        service,
        setSearchParams,
        snapshotId,
        sortBy,
        sortDirection,
        updateSort,
    ]);
    const taskListListProps = React.useMemo(
        () => ({
            version: {
                columnResizeVersion,
                lastViewedTaskId,
                nonActionablePullRequestIds,
                resolvingPullRequestIds,
            },
        }),
        [columnResizeVersion, lastViewedTaskId, nonActionablePullRequestIds, resolvingPullRequestIds],
    );

    const commandItems = React.useMemo<ICommandBarItemProps[]>(() => [
        {
            key: 'back',
            text: 'Back to lists',
            iconProps: { iconName: 'Back' },
            onClick: () => navigate(`/service/${serviceName}/agenttasks`),
        },
        {
            key: 'refresh',
            text: 'Refresh',
            iconProps: { iconName: 'Refresh' },
            onClick: () => {
                setResolvingPullRequestIds(new Set<number>());
                setNonActionablePullRequestIds(new Set<number>());
                loadDetail();
                loadTasks();
            },
        },
        {
            key: 'pr-dashboard',
            text: 'View PR Dashboard',
            iconProps: { iconName: 'BranchPullRequest' },
            onClick: () => navigate(
                `/service/${serviceName}/agenttasks/autopr?taskListId=${numericTaskListId}`,
            ),
        },
        {
            key: 'snapshot',
            text: 'Create snapshot',
            iconProps: { iconName: 'Add' },
            onClick: () => setSnapshotPanelOpen(true),
        },
        {
            key: 'enqueue',
            text: 'Enqueue selected',
            iconProps: { iconName: 'Play' },
            disabled: selectedTaskIds.length === 0 || !currentSnapshot,
            onClick: () => setEnqueuePanelOpen(true),
        },
        {
            key: 'signoff',
            text: 'Update selected PR status',
            iconProps: { iconName: 'Completed' },
            disabled: selectedTaskIds.length === 0 || !currentSnapshot,
            onClick: () => setBatchUpdatePanelOpen(true),
        },
        {
            key: 'questions',
            text: 'Open questions',
            iconProps: { iconName: 'Help' },
            onClick: () => navigate(`/service/${serviceName}/agenttasks/questions?taskListId=${numericTaskListId}`),
        },
        {
            key: 'changes',
            text: 'Proposed changes',
            iconProps: { iconName: 'BulletedList' },
            onClick: () => navigate(`/service/${serviceName}/agenttasks/changes?taskListId=${numericTaskListId}`),
        },
        {
            key: 'dashboard',
            text: 'Task Dashboard',
            iconProps: { iconName: 'ViewDashboard' },
            onClick: () => navigate(`/service/${serviceName}/agenttasks/dashboard`),
        },
        {
            key: 'summary',
            text: 'Summary',
            iconProps: { iconName: 'ReportDocument' },
            onClick: () => navigate(`/service/${serviceName}/agenttasks/summary`),
        },
        {
            key: 'definitions',
            text: 'Job definitions',
            iconProps: { iconName: 'Edit' },
            onClick: () => navigate(`/service/${serviceName}/agenttasks/definitions`),
        },
    ].filter(item => !isRegressionList || !['snapshot', 'enqueue', 'signoff'].includes(item.key)), [
        currentSnapshot,
        isRegressionList,
        loadDetail,
        loadTasks,
        navigate,
        numericTaskListId,
        selectedTaskIds.length,
        serviceName,
    ]);

    return (
        <PageShell title={`Task List${detail ? ` - ${detail.taskList.name}` : ''}`}>
            <SyntheticBanner visible={!!detail?.isSynthetic || !!tasksResult?.isSynthetic} />

            {detailError && (
                <MessageBar messageBarType={MessageBarType.error}>{detailError}</MessageBar>
            )}

            {detailLoading ? (
                <Spinner size={SpinnerSize.large} label="Loading task list..." />
            ) : detail && (
                <>
                    <Stack horizontal wrap tokens={{ childrenGap: 12 }} styles={{ root: { rowGap: 12 } }}>
                        <StatCard title="Task type" value={getTaskTypeLabel(detail.taskList.taskType)} subtitle={detail.taskList.processName} />
                        <StatCard title="Tasks" value={String(detail.taskList.taskCount)} subtitle={`Version ${detail.taskList.version}`} />
                        <StatCard title="Snapshots" value={String(detail.taskList.snapshotCount)} subtitle={`Generation ${detail.taskList.generation}`} />
                        <StatCard title="Granularity" value={isRegressionList ? 'N/A' : formatGranularity(detail.taskList.granularity)} />
                        <StatCard title="Pending work items" value={String(detail.pendingWorkItemCount)} />
                        <StatCard title="Completed work items" value={String(detail.completedWorkItemCount)} />
                    </Stack>

                    <CommandBar items={commandItems} />

                    <Stack horizontal wrap tokens={{ childrenGap: 12 }} styles={{ root: { rowGap: 12 } }} verticalAlign="end">
                        {!isRegressionList && <div style={{ minWidth: 260 }}>
                            <Dropdown
                                label="Snapshot"
                                options={detail.snapshots.map(snapshot => ({
                                    key: snapshot.snapshotId,
                                    text: `${snapshot.name || formatCompactDate(snapshot.date)} (#${snapshot.snapshotId})`,
                                }))}
                                selectedKey={snapshotId ?? detail.latestSnapshot?.snapshotId}
                                onChange={(_, option) => updateSearchParams(searchParams, setSearchParams, {
                                    snapshotId: Number(option?.key),
                                    taskId: null,
                                    prId: null,
                                    pageNumber: 0,
                                })}
                            />
                        </div>}
                        <div style={{ minWidth: 260 }}>
                            <TextField
                                label={isRegressionList ? 'Search commit' : 'Search scope'}
                                value={searchText}
                                onChange={(_, value) => setSearchText(value ?? '')}
                                onKeyDown={event => {
                                    if (event.key === 'Enter') {
                                        updateSearchParams(searchParams, setSearchParams, {
                                            search: searchText,
                                            searchMode: null,
                                            pageNumber: 0,
                                        });
                                    }
                                }}
                            />
                        </div>
                        <div style={{ minWidth: 220 }}>
                            <Dropdown
                                label="Task status"
                                options={taskStatusOptions}
                                styles={STATUS_FILTER_DROPDOWN_STYLES}
                                selectedKey={excludeDoneTasks ? 'allButDone' : taskStatusFilter ?? 'all'}
                                onRenderTitle={options => <span>{options?.[0]?.text}</span>}
                                onChange={(_, option) => updateSearchParams(searchParams, setSearchParams, {
                                    taskStatus: option?.key === 'all' ? null : String(option?.key),
                                    pageNumber: 0,
                                })}
                            />
                        </div>
                        <div style={{ minWidth: 260 }}>
                            <Dropdown
                                label="Job"
                                options={jobOptions}
                                selectedKey={selectedJobName ?? 'all'}
                                onChange={(_, option) => updateSearchParams(searchParams, setSearchParams, {
                                    job: option?.key === 'all' ? null : String(option?.key),
                                    jobStatus: null,
                                    pageNumber: 0,
                                })}
                            />
                        </div>
                        {selectedJobName && (
                            <div style={{ minWidth: 220 }}>
                                <Dropdown
                                    label="Job status"
                                    options={jobStatusOptions}
                                    selectedKey={jobStatusFilter ?? 'all'}
                                    onChange={(_, option) => updateSearchParams(searchParams, setSearchParams, {
                                        jobStatus: option?.key === 'all' ? null : Number(option?.key),
                                        pageNumber: 0,
                                    })}
                                />
                            </div>
                        )}
                        <div style={{ minWidth: 240 }}>
                            <Dropdown
                                label="PR status"
                                options={pullRequestStatusOptions}
                                styles={STATUS_FILTER_DROPDOWN_STYLES}
                                selectedKey={excludeCompletedPullRequests
                                    ? 'allButCompleted'
                                    : pullRequestStatusFilter ?? 'all'}
                                onRenderTitle={options => <span>{options?.[0]?.text}</span>}
                                onChange={(_, option) => updateSearchParams(searchParams, setSearchParams, {
                                    pullRequestStatus: option?.key === 'all' ? null : String(option?.key),
                                    pageNumber: 0,
                                })}
                            />
                        </div>
                        <div style={{ minWidth: 120 }}>
                            <Dropdown
                                label="Page size"
                                options={PAGE_SIZE_OPTIONS}
                                selectedKey={pageSize}
                                onChange={(_, option) => updateSearchParams(searchParams, setSearchParams, {
                                    pageSize: Number(option?.key),
                                    pageNumber: 0,
                                })}
                            />
                        </div>
                    </Stack>

                    {tasksError && (
                        <MessageBar messageBarType={MessageBarType.error}>{tasksError}</MessageBar>
                    )}
                    {repairSuccess && (
                        <MessageBar
                            messageBarType={MessageBarType.success}
                            onDismiss={() => setRepairSuccess(null)}
                        >
                            {repairSuccess}
                        </MessageBar>
                    )}
                    {repairError && (
                        <MessageBar
                            messageBarType={MessageBarType.error}
                            onDismiss={() => setRepairError(null)}
                        >
                            {repairError}
                        </MessageBar>
                    )}

                    {tasksLoading ? (
                        <Spinner size={SpinnerSize.large} label="Loading tasks..." />
                    ) : hasNoDirectScopeMatches && !activeReferenceResult ? (
                        <Stack tokens={{ childrenGap: 8 }} styles={{ root: { alignItems: 'flex-start' } }}>
                            {referenceError && (
                                <MessageBar messageBarType={MessageBarType.error}>{referenceError}</MessageBar>
                            )}
                            {referenceLoading ? (
                                <Spinner size={SpinnerSize.medium} label="Finding referencing tasks..." />
                            ) : (
                                <>
                                    <Text>No task scopes match this search.</Text>
                                    <DefaultButton
                                        text={`Find tasks referencing ${search.trim()} function`}
                                        disabled={!currentSnapshot}
                                        onClick={() => {
                                            if (searchMode === 'references') {
                                                void findReferencingTasks(true);
                                                return;
                                            }

                                            updateSearchParams(searchParams, setSearchParams, {
                                                searchMode: 'references',
                                                pageNumber: 0,
                                            });
                                        }}
                                    />
                                </>
                            )}
                        </Stack>
                    ) : activeReferenceResult?.totalCount === 0 ? (
                        <MessageBar messageBarType={MessageBarType.info}>
                            No tasks reference a matching profile frame.
                        </MessageBar>
                    ) : (
                        <>
                            {referenceResultsHiddenByFilters && (
                                <MessageBar messageBarType={MessageBarType.warning}>
                                    Referencing tasks were found, but the current job or status filters hide them.
                                </MessageBar>
                            )}
                            <Text variant="medium">
                                Page {currentPage} of {totalPages} • {matchingTaskCount} matching of {loadedTaskCount} tasks
                                {activeReferenceResult ? ' • referencing-task results' : ''}
                                {selectedTaskIds.length > 0 ? ` • ${selectedTaskIds.length} selected` : ''}
                            </Text>
                            {/* Justified layout redistributes drag width; fixed layout lets columns grow and scroll. */}
                            <div
                                data-testid="task-list-scroll-container"
                                style={{ overflowX: 'auto' }}
                                onMouseMoveCapture={handleTaskListMouseMove}
                            >
                                <DetailsList
                                    items={visibleTaskRows}
                                    columns={columns}
                                    selection={selection}
                                    selectionMode={SelectionMode.multiple}
                                    selectionZoneProps={{ selectionClearedOnSurfaceClick: false }}
                                    onRenderField={renderCopyableDetailsField}
                                    onRenderRow={renderTaskRow}
                                    onColumnResize={handleTaskListColumnResize}
                                    layoutMode={DetailsListLayoutMode.fixedColumns}
                                    listProps={taskListListProps}
                                    onShouldVirtualize={() => false}
                                />
                            </div>
                            <Stack horizontal tokens={{ childrenGap: 8 }}>
                                <DefaultButton
                                    text="Previous"
                                    disabled={effectivePageNumber <= 0}
                                    onClick={() => updateSearchParams(searchParams, setSearchParams, { pageNumber: Math.max(0, effectivePageNumber - 1) })}
                                />
                                <DefaultButton
                                    text="Next"
                                    disabled={effectivePageNumber + 1 >= totalPages}
                                    onClick={() => updateSearchParams(searchParams, setSearchParams, { pageNumber: effectivePageNumber + 1 })}
                                />
                            </Stack>
                        </>
                    )}

                    {!isRegressionList && <CreateSnapshotPanel
                        isOpen={isSnapshotPanelOpen}
                        serviceName={serviceName ?? ''}
                        service={service}
                        detail={detail}
                        onDismiss={() => setSnapshotPanelOpen(false)}
                        onCreated={snapshot => {
                            setSnapshotPanelOpen(false);
                            updateSearchParams(searchParams, setSearchParams, {
                                snapshotId: snapshot.snapshotId,
                                taskId: null,
                                prId: null,
                                pageNumber: 0,
                            });
                            loadDetail();
                            loadTasks();
                        }}
                    />}
                    <EnqueueTasksPanel
                        isOpen={isEnqueuePanelOpen}
                        serviceName={serviceName ?? ''}
                        taskList={detail}
                        snapshot={currentSnapshot}
                        taskIds={selectedTaskIds}
                        onDismiss={() => setEnqueuePanelOpen(false)}
                        onEnqueued={() => {
                            loadDetail();
                            loadTasks();
                        }}
                    />
                    <BatchUpdateWorkItemsPanel
                        isOpen={isBatchUpdatePanelOpen}
                        serviceName={serviceName ?? ''}
                        taskListId={detail.taskList.taskListId}
                        snapshot={currentSnapshot}
                        taskIds={selectedTaskIds}
                        onDismiss={() => setBatchUpdatePanelOpen(false)}
                        onUpdated={() => {
                            setBatchUpdatePanelOpen(false);
                            loadDetail();
                            loadTasks();
                        }}
                    />
                </>
            )}

            {serviceName && Number.isFinite(numericTaskListId) && (
                <TaskDetailPanel
                    isOpen={taskId !== undefined && prId === undefined}
                    serviceName={serviceName}
                    taskListId={numericTaskListId}
                    taskListDisplayName={detail?.taskList.name}
                    taskId={taskId}
                    snapshotId={snapshotId}
                    onProposalCountsChanged={updateTaskProposalCounts}
                    hasPreviousTask={viewedTaskIndex > 0}
                    hasNextTask={viewedTaskIndex >= 0 && viewedTaskIndex + 1 < filteredTaskRows.length}
                    onNavigatePreviousTask={() => navigateToTaskIndex(viewedTaskIndex - 1, 'previous')}
                    onNavigateNextTask={() => navigateToTaskIndex(viewedTaskIndex + 1, 'next')}
                    prefetchedTask={taskId === undefined
                        ? undefined
                        : prefetchedTaskDetails.current.get(getTaskDetailCacheKey(
                            taskId,
                            getTaskRowSnapshotId(filteredTaskRows[viewedTaskIndex]),
                        ))}
                    onDismiss={() => {
                        setTaskNavigationDirection(undefined);
                        updateSearchParams(
                            searchParams,
                            setSearchParams,
                            isRegressionList
                                ? { taskId: null, prId: null, snapshotId: null, taskTab: null }
                                : { taskId: null, prId: null, taskTab: null },
                        );
                    }}
                />
            )}
            {serviceName && Number.isFinite(numericTaskListId) && (
                <PullRequestDetailPanel
                    isOpen={taskId !== undefined && prId !== undefined}
                    serviceName={serviceName}
                    service={service}
                    taskListId={numericTaskListId}
                    taskId={taskId}
                    prId={prId}
                    isResolving={prId !== undefined && resolvingPullRequestIds.has(prId)}
                    isNotActionable={prId !== undefined && nonActionablePullRequestIds.has(prId)}
                    onResolutionStateChange={updatePullRequestResolutionState}
                    onDismiss={() => {
                        if (returnToAutoPr) {
                            navigate(returnToAutoPr);
                            return;
                        }
                        updateSearchParams(searchParams, setSearchParams, {
                            taskId: null,
                            prId: null,
                        });
                    }}
                    onResolved={loadTasks}
                />
            )}
        </PageShell>
    );
}
